(() => {
  if (customElements.get('product-configurator')) return;

  const LAYOUT_LABELS = {
    balanced: 'Balanced',
    'name-focus': 'Name focus',
    'logo-focus': 'Logo focus'
  };

  const roundHalf = (value) => Math.round(value * 2) / 2;
  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const roundToStep = (value, minimum, step) => minimum + Math.round((value - minimum) / step) * step;

  const DEFAULT_MESSAGES = {
    customizedProduct: 'Customized [product]', safeMargin: 'Dashed line: [margin] mm safe margin', layout: 'Layout', color: 'Color',
    nameAndText: '[name] & text', name: 'Name', font: 'Font', textColor: 'Text color', logo: 'Logo', noLogo: 'No logo',
    noFlag: 'No flag', maximumCharacters: 'Maximum [maximum] characters. The complete text and flag must remain inside the safe area.',
    left: 'Left', center: 'Center', right: 'Right', yourName: 'YOUR NAME', loadingFailed: 'The customizer could not be loaded. Please try again.',
    notConfigured: 'The product customizer is not configured.', serviceUnavailable: 'The product configuration service is unavailable.',
    noProfile: 'This product has no published customizer profile.', designServiceMissing: 'The design service is not configured. Please contact us.',
    saving: 'Saving your finished design…', saved: 'Design saved. Adding it to your cart…', saveFailed: 'The design could not be saved.',
    changedWhileSaving: 'The design changed while it was being saved. Please add it to the cart again.', enterName: 'Enter a name to confirm its fit.',
    nameTooLong: 'This name is too long for the selected font and layout. Shorten it or choose a narrower font.',
    autoReduced: 'Automatically reduced to [size] mm to stay inside the [margin] mm safe area.', fitOk: 'Text fits inside the [margin] mm safe area.',
    invalidName: 'Name is too long for the selected design.', asEntered: 'As entered', uppercase: 'Uppercase', rightOfName: 'Right of name', leftOfName: 'Left of name',
    directEditHint: 'Tap text or logo to move and resize it.', editText: 'Edit text', resetPosition: 'Reset position',
    textSelected: 'Text selected', logoSelected: 'Logo selected'
  };

  class ProductConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.update();
        return;
      }

      this.initialized = true;
      this.locale = (this.dataset.locale || document.documentElement.lang || window.Shopify?.locale || 'en').replace('_', '-');
      try {
        this.messages = JSON.parse(this.querySelector('[data-configurator-i18n]')?.textContent || '{}');
      } catch (_) {
        this.messages = {};
      }
      this.designId = this.createDesignId();
      this.surface = this.querySelector('.product-configurator__surface');
      this.previewStage = this.querySelector('[data-preview-stage]');
      this.preview = this.querySelector('[data-preview]');
      this.previewExpand = this.querySelector('[data-preview-expand]');
      this.previewClose = this.querySelector('[data-preview-close]');
      this.previewBackdrop = this.querySelector('[data-preview-backdrop]');
      this.previewName = this.querySelector('.product-configurator__preview-name');
      this.previewFlag = this.querySelector('.product-configurator__preview-flag');
      this.previewLogo = this.querySelector('.product-configurator__preview-logo');
      this.textHitArea = this.querySelector('[data-text-hit-area]');
      this.logoHitArea = this.querySelector('[data-logo-hit-area]');
      this.artwork = this.querySelector('.product-configurator__artwork');
      this.selectionLayer = this.querySelector('[data-selection-layer]');
      this.selectionBox = this.querySelector('[data-selection-box]');
      this.resizeHandles = [...this.querySelectorAll('[data-resize-handle]')];
      this.snapGuideX = this.querySelector('[data-snap-guide-x]');
      this.snapGuideY = this.querySelector('[data-snap-guide-y]');
      this.directToolbar = this.querySelector('[data-direct-toolbar]');
      this.selectionLabel = this.querySelector('[data-selection-label]');
      this.editTextButton = this.querySelector('[data-edit-text]');
      this.resetPositionButton = this.querySelector('[data-reset-position]');
      this.directTextInput = this.querySelector('[data-direct-text-input]');
      this.fitStatus = this.querySelector('[data-fit-status]');
      this.saveStatus = this.querySelector('[data-save-status]');
      this.nameInput = this.querySelector('[data-configurator-name]');
      this.fontSizeInput = this.querySelector('[data-configurator-font-size]');
      this.fontSizeOutput = this.querySelector('[data-font-size-output]');
      this.logoSizeInput = this.querySelector('[data-configurator-logo-size]');
      this.logoSizeOutput = this.querySelector('[data-logo-size-output]');
      this.uppercaseInput = this.querySelector('[data-configurator-uppercase]');
      this.flagInput = this.querySelector('[data-configurator-flag]');
      this.flagPicker = this.querySelector('[data-flag-picker]');
      this.flagToggle = this.querySelector('[data-flag-toggle]');
      this.flagPanel = this.querySelector('[data-flag-panel]');
      this.flagSearch = this.querySelector('[data-flag-search]');
      this.flagOptions = this.querySelector('[data-flag-options]');
      this.flagEmpty = this.querySelector('[data-flag-empty]');
      this.fontAssetUrls = new Map([...this.querySelectorAll('[data-configurator-font]')]
        .map((input) => [input.dataset.fontFile || '', input.dataset.fontUrl || '']));
      this.logoAssetUrls = new Map([...this.querySelectorAll('[data-logo-url]')]
        .map((input) => [input.dataset.logoFile || '', input.dataset.logoUrl || '']));
      this.refreshControlReferences();

      this.propertyInputs = {
        color: this.querySelector('[data-property-color]'),
        name: this.querySelector('[data-property-name]'),
        textColor: this.querySelector('[data-property-text-color]'),
        font: this.querySelector('[data-property-font]'),
        fontSize: this.querySelector('[data-property-font-size]'),
        alignment: this.querySelector('[data-property-alignment]'),
        textCase: this.querySelector('[data-property-case]'),
        layout: this.querySelector('[data-property-layout]'),
        flag: this.querySelector('[data-property-flag]'),
        flagSide: this.querySelector('[data-property-flag-side]'),
        logo: this.querySelector('[data-property-logo]'),
        logoSize: this.querySelector('[data-property-logo-size]'),
        id: this.querySelector('[data-property-id]'),
        design: this.querySelector('[data-property-design]'),
        preview: this.querySelector('[data-property-preview]')
      };

      this.form = document.getElementById(this.propertyInputs.name?.getAttribute('form'));
      this.boundInput = (event) => this.handleInput(event);
      this.boundDocumentClick = (event) => this.handleDocumentClick(event);
      this.boundKeydown = (event) => this.handleKeydown(event);
      this.boundSubmit = (event) => this.handleSubmit(event);
      this.boundPreviewPosition = () => {
        this.queuePreviewPositionUpdate();
        this.updateSelection();
        this.positionDirectTextEditor();
      };
      this.boundPreviewExpand = () => this.openExpandedPreview();
      this.boundPreviewClose = () => this.closeExpandedPreview();
      this.boundPointerDown = (event) => this.handleArtworkPointerDown(event);
      this.boundPointerMove = (event) => this.handleArtworkPointerMove(event);
      this.boundPointerUp = (event) => this.handleArtworkPointerUp(event);
      this.boundDirectTextBlur = () => this.closeDirectTextEditor();
      this.boundDirectTextKeydown = (event) => this.handleDirectTextKeydown(event);
      this.isSaving = false;
      this.allowNextSubmit = false;
      this.savedFingerprint = '';
      this.previewExpanded = false;
      this.previewPositionFrame = 0;
      this.profileReady = false;
      this.placement = {textX: null, textY: null, logoX: null, logoY: null};
      this.selectedElement = '';
      this.dragOperation = null;
      this.lastState = null;

      this.addEventListener('input', this.boundInput);
      this.addEventListener('change', this.boundInput);
      document.addEventListener('click', this.boundDocumentClick);
      document.addEventListener('keydown', this.boundKeydown);
      this.form?.addEventListener('submit', this.boundSubmit, true);
      window.addEventListener('scroll', this.boundPreviewPosition, {passive: true});
      window.addEventListener('resize', this.boundPreviewPosition, {passive: true});
      this.previewExpand?.addEventListener('click', this.boundPreviewExpand);
      this.previewClose?.addEventListener('click', this.boundPreviewClose);
      this.previewBackdrop?.addEventListener('click', this.boundPreviewClose);
      this.artwork?.addEventListener('pointerdown', this.boundPointerDown);
      this.artwork?.addEventListener('pointermove', this.boundPointerMove);
      this.artwork?.addEventListener('pointerup', this.boundPointerUp);
      this.artwork?.addEventListener('pointercancel', this.boundPointerUp);
      this.resizeHandles.forEach((handle) => {
        handle.addEventListener('pointerdown', this.boundPointerDown);
        handle.addEventListener('pointermove', this.boundPointerMove);
        handle.addEventListener('pointerup', this.boundPointerUp);
        handle.addEventListener('pointercancel', this.boundPointerUp);
      });
      this.editTextButton?.addEventListener('click', () => this.openDirectTextEditor());
      this.resetPositionButton?.addEventListener('click', () => this.resetSelectedPosition());
      this.directTextInput?.addEventListener('blur', this.boundDirectTextBlur);
      this.directTextInput?.addEventListener('keydown', this.boundDirectTextKeydown);

      this.flagToggle?.addEventListener('click', () => this.toggleFlagPanel());
      this.flagSearch?.addEventListener('input', () => this.filterFlags());
      this.propertyInputs.id.value = '';
      this.loadProfile().then((profile) => {
        this.profile = profile;
        this.applyProfile(profile);
        this.refreshControlReferences();
        this.buildFlagPicker();
        this.profileReady = true;
        this.update();
        this.queuePreviewPositionUpdate();
      }).catch((error) => {
        this.setSaveStatus(error.message || this.message('loadingFailed'), 'error');
        this.setFormSaving(true);
      });
    }

    disconnectedCallback() {
      this.removeEventListener('input', this.boundInput);
      this.removeEventListener('change', this.boundInput);
      document.removeEventListener('click', this.boundDocumentClick);
      document.removeEventListener('keydown', this.boundKeydown);
      this.form?.removeEventListener('submit', this.boundSubmit, true);
      window.removeEventListener('scroll', this.boundPreviewPosition);
      window.removeEventListener('resize', this.boundPreviewPosition);
      this.previewExpand?.removeEventListener('click', this.boundPreviewExpand);
      this.previewClose?.removeEventListener('click', this.boundPreviewClose);
      this.previewBackdrop?.removeEventListener('click', this.boundPreviewClose);
      this.artwork?.removeEventListener('pointerdown', this.boundPointerDown);
      this.artwork?.removeEventListener('pointermove', this.boundPointerMove);
      this.artwork?.removeEventListener('pointerup', this.boundPointerUp);
      this.artwork?.removeEventListener('pointercancel', this.boundPointerUp);
      this.resizeHandles.forEach((handle) => {
        handle.removeEventListener('pointerdown', this.boundPointerDown);
        handle.removeEventListener('pointermove', this.boundPointerMove);
        handle.removeEventListener('pointerup', this.boundPointerUp);
        handle.removeEventListener('pointercancel', this.boundPointerUp);
      });
      this.directTextInput?.removeEventListener('blur', this.boundDirectTextBlur);
      this.directTextInput?.removeEventListener('keydown', this.boundDirectTextKeydown);
      if (this.previewPositionFrame) cancelAnimationFrame(this.previewPositionFrame);
      this.closeExpandedPreview(false);
    }

    createDesignId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID().split('-')[0].toUpperCase();
      return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`.toUpperCase();
    }

    message(key, replacements = {}) {
      let value = this.messages?.[key] || DEFAULT_MESSAGES[key] || key;
      Object.entries(replacements).forEach(([name, replacement]) => {
        value = value.replaceAll(`[${name}]`, String(replacement));
      });
      return value;
    }

    localize(translations, fallback = '') {
      if (!translations || typeof translations !== 'object') return fallback;
      const exact = Object.entries(translations).find(([locale, value]) => locale.toLowerCase() === this.locale.toLowerCase() && value?.trim());
      if (exact) return exact[1];
      const base = this.locale.split('-')[0].toLowerCase();
      const baseMatch = Object.entries(translations).find(([locale, value]) => locale.toLowerCase() === base && value?.trim());
      if (baseMatch) return baseMatch[1];
      const defaultLocale = this.profile?.defaultLocale?.toLowerCase();
      const defaultMatch = Object.entries(translations).find(([locale, value]) => locale.toLowerCase() === defaultLocale && value?.trim());
      return defaultMatch?.[1] || Object.values(translations).find((value) => value?.trim()) || fallback;
    }

    optionLabel(option, fallback = '') {
      return this.localize(option?.labels, option?.label || fallback);
    }

    refreshControlReferences() {
      this.colorInputs = this.querySelectorAll('[data-color-value]');
      this.textColorInputs = this.querySelectorAll('[data-text-color-value]');
      this.logoInputs = this.querySelectorAll('[data-logo-url]');
      this.fontInputs = this.querySelectorAll('[data-configurator-font]');
      this.layoutInputs = this.querySelectorAll('[data-configurator-layout]');
      this.alignmentInputs = this.querySelectorAll('[data-configurator-alignment]');
      this.flagSideInputs = this.querySelectorAll('[data-configurator-flag-side]');
    }

    async loadProfile() {
      const baseUrl = (this.dataset.profileBaseUrl || '').replace(/\/$/, '');
      if (!baseUrl) throw new Error(this.message('notConfigured'));

      const lookups = [this.dataset.shopifyProductId].filter(Boolean);
      for (const lookup of lookups) {
        const response = await fetch(`${baseUrl}/${encodeURIComponent(lookup)}`, {
          method: 'GET', mode: 'cors', credentials: 'omit', headers: {'Accept': 'application/json'}
        });
        if (response.status === 404) continue;
        if (!response.ok) throw new Error(this.message('serviceUnavailable'));
        return response.json();
      }

      throw new Error(this.message('noProfile'));
    }

    applyProfile(profile) {
      this.dataset.productKey = profile.productKey;
      this.dataset.widthMm = String(profile.widthMm);
      this.dataset.heightMm = String(profile.heightMm);
      this.dataset.safeMarginMm = String(profile.safeMarginMm);
      const title = this.querySelector('.product-configurator__title');
      const intro = this.querySelector('.product-configurator__intro');
      if (title) title.textContent = this.localize(profile.titles, title.textContent);
      if (intro) intro.textContent = this.localize(profile.descriptions, intro.textContent);

      const artwork = this.querySelector('.product-configurator__artwork');
      artwork?.setAttribute('viewBox', `0 0 ${profile.widthMm} ${profile.heightMm}`);
      const artworkTitle = artwork?.querySelector('title');
      if (artworkTitle) artworkTitle.textContent = this.message('customizedProduct', {product: profile.displayName});
      this.surface?.setAttribute('d', profile.surfacePath);
      this.surface?.setAttribute('transform', profile.surfaceTransform || '');
      this.querySelectorAll('.product-configurator__surface-shading, .product-configurator__template-line, .product-configurator__outline').forEach((path) => {
        path.setAttribute('d', profile.surfacePath);
        path.setAttribute('transform', profile.surfaceTransform || '');
      });
      const shading = this.querySelector('.product-configurator__surface-shading');
      if (shading) shading.hidden = profile.productColors.mode === 0;

      const safeArea = this.querySelector('.product-configurator__safe-area');
      const clipRect = this.querySelector('clipPath rect');
      [safeArea, clipRect].forEach((rect) => {
        if (!rect) return;
        rect.setAttribute('x', String(profile.safeMarginMm));
        rect.setAttribute('y', String(profile.safeMarginMm));
        rect.setAttribute('width', String(profile.widthMm - profile.safeMarginMm * 2));
        rect.setAttribute('height', String(profile.heightMm - profile.safeMarginMm * 2));
        rect.setAttribute('rx', String(profile.safeCornerRadius || 0));
      });
      if (this.snapGuideX) {
        this.snapGuideX.setAttribute('x1', String(profile.widthMm / 2));
        this.snapGuideX.setAttribute('x2', String(profile.widthMm / 2));
        this.snapGuideX.setAttribute('y1', String(profile.safeMarginMm));
        this.snapGuideX.setAttribute('y2', String(profile.heightMm - profile.safeMarginMm));
      }
      if (this.snapGuideY) {
        this.snapGuideY.setAttribute('x1', String(profile.safeMarginMm));
        this.snapGuideY.setAttribute('x2', String(profile.widthMm - profile.safeMarginMm));
        this.snapGuideY.setAttribute('y1', String(profile.heightMm / 2));
        this.snapGuideY.setAttribute('y2', String(profile.heightMm / 2));
      }

      const note = this.querySelector('.product-configurator__safe-note');
      if (note) note.textContent = this.message('safeMargin', {margin: profile.safeMarginMm});

      this.setHeading('[data-feature-layouts] > legend', this.localize(profile.layouts.labels, profile.layouts.label || this.message('layout')));
      this.setHeading('[data-feature-product-colors] > legend', this.localize(profile.productColors.labels, profile.productColors.label || this.message('color')));
      this.setHeading('[data-feature-text] > legend', this.message('nameAndText', {name: this.localize(profile.text.labels, profile.text.label || this.message('name'))}));
      this.setHeading('[data-feature-fonts] > legend', this.localize(profile.fonts.labels, profile.fonts.label || this.message('font')));
      this.setHeading('[data-feature-text-colors] > legend', this.localize(profile.textColors.labels, profile.textColors.label || this.message('textColor')));
      this.setHeading('[data-feature-logos] > legend', this.localize(profile.logos.labels, profile.logos.label || this.message('logo')));

      this.buildLayouts(profile.layouts);
      this.buildColors(profile.productColors, '[data-product-color-options]', 'data-color-value');
      this.buildColors(profile.textColors, '[data-text-color-options]', 'data-text-color-value');
      this.buildFonts(profile.fonts);
      this.buildLogos(profile.logos);
      this.configureText(profile.text);
      this.configureFlags(profile.flags);
      this.setFeatureVisibility('[data-feature-layouts]', profile.layouts.mode === 2);
      this.setFeatureVisibility('[data-feature-product-colors]', profile.productColors.mode === 2);
      this.setFeatureVisibility('[data-feature-text]', profile.text.mode !== 0);
      this.setFeatureVisibility('[data-feature-name]', profile.text.mode === 2);
      this.setFeatureVisibility('[data-feature-fonts]', profile.fonts.mode === 2);
      this.setFeatureVisibility('[data-feature-text-colors]', profile.textColors.mode === 2);
      this.setFeatureVisibility('[data-feature-flags]', profile.flags.mode === 2);
      this.setFeatureVisibility('[data-feature-logos]', profile.logos.mode === 2);
      this.setFeatureVisibility('[data-feature-text-size]', profile.text.mode !== 0 && profile.text.allowResize);
      this.setFeatureVisibility('[data-feature-logo-size]', profile.logos.mode !== 0 && profile.logos.options.length > 0 && profile.logos.allowResize);
      const directHint = this.querySelector('[data-direct-edit-hint]');
      if (directHint) {
        directHint.textContent = this.message('directEditHint');
        directHint.hidden = !(profile.text.allowMove || profile.text.allowResize || profile.logos.allowMove || profile.logos.allowResize || profile.text.mode === 2);
      }
      this.renumberVisibleSections();
    }

    setFeatureVisibility(selector, visible) {
      const element = this.querySelector(selector);
      if (element) element.hidden = !visible;
    }

    setHeading(selector, label) {
      const element = this.querySelector(selector);
      if (!element) return;
      element.dataset.featureLabel = label;
      element.textContent = label;
    }

    buildLayouts(feature) {
      const container = this.querySelector('[data-layout-options]');
      if (!container) return;
      container.replaceChildren();
      feature.options.forEach((option) => {
        const optionLabel = this.optionLabel(option, option.key);
        const input = this.createRadio('layout', option.key, option.key === feature.defaultKey);
        input.dataset.configuratorLayout = '';
        input.dataset.optionLabel = optionLabel;
        ['textY', 'textYWithoutLogo', 'logoX', 'logoY', 'logoWidth', 'logoHeight'].forEach((key) => {
          input.dataset[key] = String(option[key]);
        });
        const label = document.createElement('label');
        label.className = 'product-configurator__layout-option';
        const span = document.createElement('span');
        span.append(this.createLayoutPreview(option), document.createTextNode(optionLabel));
        label.append(input, span);
        container.appendChild(label);
      });
    }

    createLayoutPreview(option) {
      const preview = document.createElement('i');
      preview.className = 'product-configurator__layout-preview';
      preview.setAttribute('aria-hidden', 'true');

      const profile = this.profile;
      const previewWidth = 86;
      const previewHeight = 44;
      const scaleX = previewWidth / Math.max(1, Number(profile.widthMm));
      const scaleY = previewHeight / Math.max(1, Number(profile.heightMm));
      const defaultColor = profile.productColors.options.find((color) => color.key === profile.productColors.defaultKey);
      preview.style.background = defaultColor?.hex || '#1769aa';
      preview.classList.toggle('is-light', this.isLightColor(defaultColor?.hex || ''));

      if (profile.text.mode !== 0) {
        const text = document.createElement('b');
        const safeWidth = Math.max(18, (profile.widthMm - profile.safeMarginMm * 2) * scaleX);
        const textWidth = Math.min(54, safeWidth * 0.68);
        text.style.left = `${(previewWidth - textWidth) / 2}px`;
        text.style.top = `${Math.max(3, Number(option.textY) * scaleY - 2.5)}px`;
        text.style.width = `${textWidth}px`;
        text.style.height = '5px';
        preview.appendChild(text);
      }

      if (profile.flags.mode !== 0) {
        const flag = document.createElement('em');
        flag.style.left = `${Math.max(4, profile.safeMarginMm * scaleX + 3)}px`;
        flag.style.top = `${Math.max(3, Number(option.textY) * scaleY - 4)}px`;
        flag.style.width = '10px';
        flag.style.height = '8px';
        preview.appendChild(flag);
      }

      if (profile.logos.mode !== 0) {
        const logo = document.createElement('strong');
        logo.style.left = `${Number(option.logoX) * scaleX}px`;
        logo.style.top = `${Number(option.logoY) * scaleY}px`;
        logo.style.width = `${Math.max(7, Number(option.logoWidth) * scaleX)}px`;
        logo.style.height = `${Math.max(5, Number(option.logoHeight) * scaleY)}px`;
        preview.appendChild(logo);
      }

      return preview;
    }

    buildColors(feature, selector, dataAttribute) {
      const container = this.querySelector(selector);
      if (!container) return;
      container.replaceChildren();
      feature.options.forEach((option) => {
        const optionLabel = this.optionLabel(option, option.key);
        const input = this.createRadio(selector, option.key, option.key === feature.defaultKey);
        input.setAttribute(dataAttribute, option.hex);
        input.dataset.optionLabel = optionLabel;
        const label = document.createElement('label');
        label.className = 'product-configurator__swatch';
        label.title = optionLabel;
        const swatch = document.createElement('span');
        swatch.style.setProperty('--swatch-color', option.hex);
        swatch.setAttribute('aria-hidden', 'true');
        const text = document.createElement('span');
        text.className = 'visually-hidden';
        text.textContent = optionLabel;
        label.append(input, swatch, text);
        container.appendChild(label);
      });
    }

    buildFonts(feature) {
      const container = this.querySelector('[data-font-options]');
      if (!container) return;
      container.replaceChildren();
      feature.options.forEach((option) => {
        const optionLabel = this.optionLabel(option, option.key);
        const input = this.createRadio('font', option.key, option.key === feature.defaultKey);
        input.dataset.configuratorFont = '';
        input.dataset.optionLabel = optionLabel;
        input.dataset.fontFamily = option.cssFamily;
        input.dataset.fontWeight = String(option.weight);
        input.dataset.fontUrl = option.assetUrl || this.fontAssetUrls.get(option.assetFileName) || '';
        const label = document.createElement('label');
        label.className = 'product-configurator__font-option';
        const span = document.createElement('span');
        span.style.setProperty('--font-preview', option.cssFamily);
        span.style.setProperty('--font-weight', String(option.weight));
        const sample = document.createElement('b');
        sample.textContent = 'Radne';
        const name = document.createElement('small');
        name.textContent = optionLabel;
        span.append(sample, name);
        label.append(input, span);
        container.appendChild(label);
      });
    }

    buildLogos(feature) {
      const container = this.querySelector('[data-logo-options]');
      if (!container) return;
      container.replaceChildren();
      const options = feature.allowNone
        ? [{key: '', labels: {[this.locale]: this.message('noLogo')}, url: '', assetFileName: ''}, ...feature.options]
        : feature.options;
      options.forEach((option) => {
        const optionLabel = this.optionLabel(option, option.key);
        const input = this.createRadio('logo', option.key, option.key === feature.defaultKey);
        input.dataset.logoUrl = option.url || this.logoAssetUrls.get(option.assetFileName) || '';
        input.dataset.optionLabel = optionLabel;
        const label = document.createElement('label');
        label.className = 'product-configurator__logo-option';
        const span = document.createElement('span');
        if (input.dataset.logoUrl) {
          const image = document.createElement('img');
          image.src = input.dataset.logoUrl;
          image.alt = optionLabel;
          image.loading = 'lazy';
          span.appendChild(image);
        } else {
          span.textContent = optionLabel;
        }
        label.append(input, span);
        container.appendChild(label);
      });

      if (this.logoSizeInput) {
        this.logoSizeInput.min = String(feature.minimumSizePercent);
        this.logoSizeInput.max = String(feature.maximumSizePercent);
        this.logoSizeInput.step = String(feature.sizeStepPercent);
        this.logoSizeInput.value = String(feature.defaultSizePercent);
      }
      if (this.previewLogo) this.previewLogo.style.cursor = feature.allowMove ? 'grab' : 'pointer';
    }

    configureText(feature) {
      if (!this.nameInput) return;
      const label = this.localize(feature.labels, feature.label || this.message('name'));
      const fieldLabel = this.querySelector('[data-text-field-label]');
      const fieldHelp = this.querySelector('[data-text-field-help]');
      if (fieldLabel) fieldLabel.textContent = label;
      if (fieldHelp) fieldHelp.textContent = this.message('maximumCharacters', {maximum: feature.maximumCharacters});
      this.nameInput.placeholder = label;
      this.nameInput.maxLength = feature.maximumCharacters;
      this.nameInput.required = feature.mode !== 0 && Boolean(feature.required);
      this.nameInput.value = feature.mode === 0 ? '' : this.localize(feature.defaultTexts, feature.defaultText || '');
      this.nameInput.readOnly = feature.mode === 1;
      if (this.directTextInput) {
        this.directTextInput.maxLength = feature.maximumCharacters;
        this.directTextInput.readOnly = feature.mode !== 2;
      }
      if (this.previewName) this.previewName.style.cursor = feature.allowMove ? 'grab' : 'pointer';
      if (this.previewFlag) this.previewFlag.style.cursor = feature.allowMove ? 'grab' : 'pointer';
      if (this.fontSizeInput) {
        this.fontSizeInput.min = String(feature.minimumSize);
        this.fontSizeInput.max = String(feature.maximumSize);
        this.fontSizeInput.step = String(feature.sizeStep);
        this.fontSizeInput.value = String(feature.defaultSize);
      }
      if (this.uppercaseInput) {
        this.uppercaseInput.checked = false;
        this.uppercaseInput.closest('label').hidden = !feature.allowUppercase;
      }

      const alignmentContainer = this.querySelector('[data-alignment-options]');
      if (alignmentContainer) {
        alignmentContainer.replaceChildren();
        feature.alignments.forEach((alignment) => {
          const input = this.createRadio('alignment', alignment, alignment === feature.defaultAlignment);
          input.dataset.configuratorAlignment = '';
          const label = document.createElement('label');
          const span = document.createElement('span');
          span.textContent = this.message(alignment);
          label.append(input, span);
          alignmentContainer.appendChild(label);
        });
      }
    }

    configureFlags(feature) {
      if (!this.flagInput) return;
      const fieldLabel = this.querySelector('[data-flag-field-label]');
      if (fieldLabel) fieldLabel.textContent = this.localize(feature.labels, feature.label || this.message('flag'));
      this.flagInput.value = feature.mode === 0 ? '' : (feature.defaultCode || '');
      this.querySelectorAll('[data-configurator-flag-side]').forEach((input) => {
        const enabled = feature.positions.includes(input.value);
        input.closest('label').hidden = !enabled;
        input.checked = input.value === feature.defaultPosition;
      });
    }

    createRadio(group, value, checked) {
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `${this.id}-${String(group).replace(/[^a-z0-9]/gi, '-')}`;
      input.value = value;
      input.checked = checked;
      return input;
    }

    renumberVisibleSections() {
      let number = 1;
      this.querySelectorAll('.product-configurator__controls > fieldset').forEach((fieldset) => {
        if (fieldset.hidden) return;
        const legend = fieldset.querySelector(':scope > legend');
        if (!legend) return;
        const label = legend.dataset.featureLabel || legend.textContent.replace(/^\d+\.\s*/, '');
        legend.textContent = `${number++}. ${label}`;
      });
    }

    queuePreviewPositionUpdate() {
      if (this.previewPositionFrame) return;
      this.previewPositionFrame = requestAnimationFrame(() => {
        this.previewPositionFrame = 0;
        this.updatePreviewPosition();
      });
    }

    updatePreviewPosition() {
      if (!this.previewStage || this.previewExpanded) return;

      const rootStyles = getComputedStyle(document.documentElement);
      const headerHeight = Number.parseFloat(rootStyles.getPropertyValue('--header-height')) || 0;
      const topOffset = headerHeight + 12;
      const stageRect = this.previewStage.getBoundingClientRect();
      const configuratorRect = this.getBoundingClientRect();
      const shouldFloat = stageRect.bottom <= topOffset && configuratorRect.bottom > topOffset + 120;
      const isFloating = this.classList.contains('is-preview-floating');

      if (shouldFloat === isFloating) return;

      if (shouldFloat) {
        this.previewStage.style.height = `${stageRect.height}px`;
        this.classList.add('is-preview-floating');
      } else {
        this.classList.remove('is-preview-floating');
        this.previewStage.style.removeProperty('height');
      }
    }

    openExpandedPreview() {
      if (!this.preview || this.previewExpanded) return;
      this.previewExpanded = true;
      this.classList.add('is-preview-expanded');
      this.previewBackdrop.hidden = false;
      this.previewClose.hidden = false;
      this.previewExpand?.setAttribute('aria-expanded', 'true');
      this.preview.setAttribute('role', 'dialog');
      this.preview.setAttribute('aria-modal', 'true');
      document.documentElement.classList.add('customizer-preview-open');
      this.previewClose.focus();
    }

    closeExpandedPreview(restoreFocus = true) {
      if (!this.previewExpanded) return;
      this.previewExpanded = false;
      this.classList.remove('is-preview-expanded');
      this.previewBackdrop.hidden = true;
      this.previewClose.hidden = true;
      this.previewExpand?.setAttribute('aria-expanded', 'false');
      this.preview?.removeAttribute('role');
      this.preview?.removeAttribute('aria-modal');
      if (!document.querySelector('product-configurator.is-preview-expanded')) {
        document.documentElement.classList.remove('customizer-preview-open');
      }
      if (restoreFocus) this.previewExpand?.focus();
      this.queuePreviewPositionUpdate();
    }

    handleInput(event) {
      if (event.target === this.flagSearch) return;
      if (event.target === this.directTextInput) {
        this.nameInput.value = this.directTextInput.value;
      }
      if (event.target?.matches?.('[data-configurator-layout]')) {
        this.placement = {textX: null, textY: null, logoX: null, logoY: null};
      } else if (event.target?.matches?.('[data-configurator-alignment]')) {
        this.placement.textX = null;
      } else if (event.target?.matches?.('[data-logo-url]')) {
        this.placement.logoX = null;
        this.placement.logoY = null;
      }
      this.invalidateStoredDesign();
      this.update();
      if (event.target === this.directTextInput) this.positionDirectTextEditor();
    }

    handleDocumentClick(event) {
      if (this.flagPanel?.hidden || this.flagPicker?.contains(event.target)) return;
      this.closeFlagPanel();
    }

    handleKeydown(event) {
      if (this.previewExpanded) {
        if (event.key === 'Escape') this.closeExpandedPreview();
        if (event.key === 'Tab') {
          event.preventDefault();
          this.previewClose?.focus();
        }
        return;
      }

      if (event.key !== 'Escape' || this.flagPanel?.hidden) return;
      this.closeFlagPanel();
      this.flagToggle?.focus();
    }

    safeBounds() {
      const margin = Number(this.dataset.safeMarginMm) || 0;
      return {
        left: margin,
        top: margin,
        right: Number(this.dataset.widthMm) - margin,
        bottom: Number(this.dataset.heightMm) - margin
      };
    }

    svgPoint(event) {
      const point = this.artwork.createSVGPoint();
      point.x = event.clientX;
      point.y = event.clientY;
      return point.matrixTransform(this.artwork.getScreenCTM().inverse());
    }

    boundsFor(kind) {
      if (kind === 'text') return this.lastState?.textBounds || null;
      if (kind !== 'logo' || !this.lastState?.logoBox) return null;
      const [x, y, width, height] = this.lastState.logoBox;
      return {x, y, width, height};
    }

    canMove(kind) {
      return kind === 'text' ? Boolean(this.profile?.text.allowMove) : Boolean(this.profile?.logos.allowMove);
    }

    canResize(kind) {
      return kind === 'text' ? Boolean(this.profile?.text.allowResize) : Boolean(this.profile?.logos.allowResize);
    }

    selectArtwork(kind) {
      this.selectedElement = kind;
      this.updateSelection();
    }

    setHitArea(element, bounds, visible) {
      if (!element) return;
      element.hidden = !visible || !bounds;
      if (!visible || !bounds) return;
      element.setAttribute('x', String(bounds.x));
      element.setAttribute('y', String(bounds.y));
      element.setAttribute('width', String(bounds.width));
      element.setAttribute('height', String(bounds.height));
    }

    updateHitAreas() {
      this.setHitArea(this.textHitArea, this.boundsFor('text'), this.profile?.text.mode !== 0);
      this.setHitArea(this.logoHitArea, this.boundsFor('logo'), Boolean(this.lastState?.logoUrl));
    }

    updateSelection() {
      const kind = this.selectedElement;
      const bounds = this.boundsFor(kind);
      const available = bounds && (kind !== 'logo' || this.lastState?.logoUrl) && (kind !== 'text' || this.profile?.text.mode !== 0);
      if (!available) {
        this.selectionLayer?.setAttribute('hidden', '');
        this.resizeHandles.forEach((handle) => { handle.hidden = true; });
        if (this.directToolbar) this.directToolbar.hidden = true;
        return;
      }

      this.selectionLayer.removeAttribute('hidden');
      this.selectionBox.setAttribute('x', String(bounds.x));
      this.selectionBox.setAttribute('y', String(bounds.y));
      this.selectionBox.setAttribute('width', String(bounds.width));
      this.selectionBox.setAttribute('height', String(bounds.height));
      const canResize = this.canResize(kind);
      this.resizeHandles.forEach((handle) => { handle.hidden = !canResize; });
      if (canResize) this.positionResizeHandles(bounds);

      if (this.directToolbar) this.directToolbar.hidden = false;
      if (this.selectionLabel) this.selectionLabel.textContent = this.message(kind === 'text' ? 'textSelected' : 'logoSelected');
      if (this.editTextButton) this.editTextButton.hidden = !(kind === 'text' && this.profile?.text.mode === 2);
      if (this.resetPositionButton) this.resetPositionButton.hidden = !this.canMove(kind);
    }

    positionResizeHandles(bounds) {
      if (!this.resizeHandles.length || !this.artwork || !this.preview || !bounds) return;
      const matrix = this.artwork.getScreenCTM?.();
      if (!matrix) {
        this.resizeHandles.forEach((handle) => { handle.hidden = true; });
        return;
      }
      const previewRect = this.preview.getBoundingClientRect();
      const corners = {
        nw: [bounds.x, bounds.y, -5, -5],
        ne: [bounds.x + bounds.width, bounds.y, 5, -5],
        se: [bounds.x + bounds.width, bounds.y + bounds.height, 5, 5],
        sw: [bounds.x, bounds.y + bounds.height, -5, 5]
      };
      this.resizeHandles.forEach((handle) => {
        const corner = corners[handle.dataset.resizeCorner];
        if (!corner) return;
        const point = this.artwork.createSVGPoint();
        point.x = corner[0];
        point.y = corner[1];
        const screenPoint = point.matrixTransform(matrix);
        handle.style.left = `${screenPoint.x - previewRect.left + corner[2]}px`;
        handle.style.top = `${screenPoint.y - previewRect.top + corner[3]}px`;
      });
    }

    resetSelectedPosition() {
      if (this.selectedElement === 'text') {
        this.placement.textX = null;
        this.placement.textY = null;
      } else if (this.selectedElement === 'logo') {
        this.placement.logoX = null;
        this.placement.logoY = null;
      }
      this.invalidateStoredDesign();
      this.update();
    }

    handleArtworkPointerDown(event) {
      if (!this.profileReady || this.directTextInput && !this.directTextInput.hidden) return;
      const target = event.target;
      const resizeHandle = target.closest?.('[data-resize-handle]');
      const resizing = Boolean(resizeHandle);
      const point = this.svgPoint(event);
      let kind = resizing
        ? this.selectedElement
        : (target === this.previewLogo || target === this.logoHitArea)
          ? 'logo'
          : (target === this.previewName || target === this.previewFlag || target === this.textHitArea ? 'text' : '');
      if (!kind) {
        const logoBounds = this.boundsFor('logo');
        const textBounds = this.boundsFor('text');
        if (logoBounds && point.x >= logoBounds.x && point.x <= logoBounds.x + logoBounds.width && point.y >= logoBounds.y && point.y <= logoBounds.y + logoBounds.height) kind = 'logo';
        else if (textBounds && point.x >= textBounds.x && point.x <= textBounds.x + textBounds.width && point.y >= textBounds.y && point.y <= textBounds.y + textBounds.height) kind = 'text';
      }
      if (!kind) return;

      const bounds = this.boundsFor(kind);
      if (!bounds) return;
      const wasSelected = this.selectedElement === kind;
      this.selectArtwork(kind);
      const action = resizing ? 'resize' : 'move';
      const permitted = resizing ? this.canResize(kind) : this.canMove(kind);
      if (!permitted) {
        if (wasSelected && kind === 'text') this.openDirectTextEditor();
        return;
      }

      this.dragOperation = {
        pointerId: event.pointerId,
        kind,
        action,
        wasSelected,
        startPoint: point,
        startBounds: {...bounds},
        startTextSize: Number(this.fontSizeInput?.value || 0),
        startLogoSize: Number(this.logoSizeInput?.value || 100),
        resizeCorner: resizeHandle?.dataset.resizeCorner || 'se',
        moved: false
      };
      this.pointerCaptureTarget = event.currentTarget || this.artwork;
      this.pointerCaptureTarget.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    }

    handleArtworkPointerMove(event) {
      const drag = this.dragOperation;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const point = this.svgPoint(event);
      const deltaX = point.x - drag.startPoint.x;
      const deltaY = point.y - drag.startPoint.y;
      drag.moved ||= Math.hypot(deltaX, deltaY) > 0.25;
      const safe = this.safeBounds();

      if (drag.action === 'move') {
        let x = clamp(drag.startBounds.x + deltaX, safe.left, safe.right - drag.startBounds.width);
        let y = clamp(drag.startBounds.y + deltaY, safe.top, safe.bottom - drag.startBounds.height);
        let centerX = x + drag.startBounds.width / 2;
        let centerY = y + drag.startBounds.height / 2;
        const snapX = Math.abs(centerX - Number(this.dataset.widthMm) / 2) <= 1.25;
        const snapY = Math.abs(centerY - Number(this.dataset.heightMm) / 2) <= 1.25;
        if (snapX) centerX = Number(this.dataset.widthMm) / 2;
        if (snapY) centerY = Number(this.dataset.heightMm) / 2;
        x = clamp(centerX - drag.startBounds.width / 2, safe.left, safe.right - drag.startBounds.width);
        y = clamp(centerY - drag.startBounds.height / 2, safe.top, safe.bottom - drag.startBounds.height);
        this.snapGuideX.toggleAttribute('hidden', !snapX);
        this.snapGuideY.toggleAttribute('hidden', !snapY);

        if (drag.kind === 'text') {
          this.placement.textX = x + drag.startBounds.width / 2;
          this.placement.textY = y + drag.startBounds.height / 2;
        } else {
          this.placement.logoX = x;
          this.placement.logoY = y;
        }
      } else {
        const fromWest = drag.resizeCorner.includes('w');
        const fromNorth = drag.resizeCorner.includes('n');
        const anchorX = fromWest ? drag.startBounds.x + drag.startBounds.width : drag.startBounds.x;
        const anchorY = fromNorth ? drag.startBounds.y + drag.startBounds.height : drag.startBounds.y;
        const startDistance = Math.max(1, Math.hypot(drag.startBounds.width, drag.startBounds.height));
        const distance = Math.max(1, Math.hypot(point.x - anchorX, point.y - anchorY));
        const maximumWidth = fromWest ? anchorX - safe.left : safe.right - anchorX;
        const maximumHeight = fromNorth ? anchorY - safe.top : safe.bottom - anchorY;
        const maximumRatio = Math.max(0.05, Math.min(maximumWidth / drag.startBounds.width, maximumHeight / drag.startBounds.height));
        const ratio = Math.min(distance / startDistance, maximumRatio);
        if (drag.kind === 'text') {
          const feature = this.profile.text;
          const size = Math.min(
            clamp(roundToStep(drag.startTextSize * ratio, Number(feature.minimumSize), Number(feature.sizeStep)), Number(feature.minimumSize), Number(feature.maximumSize)),
            drag.startTextSize * maximumRatio
          );
          const actualRatio = size / drag.startTextSize;
          const width = drag.startBounds.width * actualRatio;
          const height = drag.startBounds.height * actualRatio;
          this.fontSizeInput.value = String(Math.round(size * 1000) / 1000);
          this.placement.textX = fromWest ? anchorX - width / 2 : anchorX + width / 2;
          this.placement.textY = fromNorth ? anchorY - height / 2 : anchorY + height / 2;
        } else {
          const feature = this.profile.logos;
          const size = Math.min(
            clamp(roundToStep(drag.startLogoSize * ratio, Number(feature.minimumSizePercent), Number(feature.sizeStepPercent)), Number(feature.minimumSizePercent), Number(feature.maximumSizePercent)),
            drag.startLogoSize * maximumRatio
          );
          const actualRatio = size / drag.startLogoSize;
          const width = drag.startBounds.width * actualRatio;
          const height = drag.startBounds.height * actualRatio;
          this.logoSizeInput.value = String(Math.round(size));
          this.placement.logoX = fromWest ? anchorX - width : anchorX;
          this.placement.logoY = fromNorth ? anchorY - height : anchorY;
        }
      }

      this.invalidateStoredDesign();
      this.update();
      event.preventDefault();
    }

    handleArtworkPointerUp(event) {
      const drag = this.dragOperation;
      if (!drag || drag.pointerId !== event.pointerId) return;
      this.dragOperation = null;
      this.snapGuideX.setAttribute('hidden', '');
      this.snapGuideY.setAttribute('hidden', '');
      this.pointerCaptureTarget?.releasePointerCapture?.(event.pointerId);
      this.pointerCaptureTarget = null;
      if (!drag.moved && drag.wasSelected && drag.kind === 'text' && drag.action === 'move') {
        this.openDirectTextEditor();
      }
    }

    openDirectTextEditor() {
      if (!this.directTextInput || this.profile?.text.mode !== 2 || !this.nameInput) return;
      this.directTextOriginal = this.nameInput.value;
      this.directTextInput.value = this.nameInput.value;
      this.directTextInput.hidden = false;
      this.positionDirectTextEditor();
      this.directTextInput.focus();
      this.directTextInput.select();
    }

    positionDirectTextEditor() {
      if (!this.directTextInput || this.directTextInput.hidden || !this.previewName || !this.preview) return;
      const textRect = this.previewName.getBoundingClientRect();
      const previewRect = this.preview.getBoundingClientRect();
      const width = clamp(textRect.width + 28, 120, Math.max(120, previewRect.width - 36));
      const left = clamp(textRect.left - previewRect.left + textRect.width / 2 - width / 2, 12, previewRect.width - width - 12);
      this.directTextInput.style.left = `${left}px`;
      this.directTextInput.style.top = `${Math.max(8, textRect.top - previewRect.top - 5)}px`;
      this.directTextInput.style.width = `${width}px`;
      this.directTextInput.style.fontFamily = this.lastState?.fontFamily || '';
      this.directTextInput.style.fontWeight = String(this.lastState?.fontWeight || 700);
      this.directTextInput.style.fontSize = `${Math.max(16, textRect.height * 0.72)}px`;
    }

    closeDirectTextEditor() {
      if (this.directTextInput) this.directTextInput.hidden = true;
    }

    handleDirectTextKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.directTextInput.blur();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.directTextInput.value = this.directTextOriginal || '';
        this.nameInput.value = this.directTextInput.value;
        this.invalidateStoredDesign();
        this.update();
        this.directTextInput.blur();
      }
    }

    handleSubmit(event) {
      if (!this.profileReady) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.setSaveStatus('Please wait for the product customizer to finish loading.', 'error');
        return;
      }

      this.update();
      if (this.invalidName) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.nameInput?.focus();
        this.fitStatus?.scrollIntoView({behavior: 'smooth', block: 'center'});
        return;
      }

      if (this.allowNextSubmit) {
        this.allowNextSubmit = false;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      if (!this.isSaving) this.saveDesignAndSubmit(event.submitter);
    }

    invalidateStoredDesign() {
      this.savedFingerprint = '';
      this.propertyInputs.id.value = '';
      this.propertyInputs.preview.value = '';
      if (this.saveStatus) {
        this.saveStatus.hidden = true;
        this.saveStatus.textContent = '';
        delete this.saveStatus.dataset.status;
      }
    }

    setSaveStatus(message, status) {
      if (!this.saveStatus) return;
      this.saveStatus.textContent = message;
      this.saveStatus.dataset.status = status;
      this.saveStatus.hidden = !message;
    }

    setFormSaving(saving) {
      this.isSaving = saving;
      this.setAttribute('aria-busy', String(saving));
      this.form?.querySelectorAll('[type="submit"]').forEach((button) => {
        if (saving) {
          button.dataset.customizerWasDisabled = button.disabled ? 'true' : 'false';
          button.disabled = true;
        } else if (button.dataset.customizerWasDisabled !== 'true') {
          button.disabled = false;
          delete button.dataset.customizerWasDisabled;
        } else {
          delete button.dataset.customizerWasDisabled;
        }
      });
    }

    designFingerprint(design) {
      return JSON.stringify({...design, id: ''});
    }

    absoluteAssetUrl(url) {
      if (!url) return '';
      try {
        return new URL(url, window.location.href).href;
      } catch (_error) {
        return '';
      }
    }

    imagePlacement(image, url) {
      if (!image || !url || image.hasAttribute('hidden')) return null;
      return {
        url: this.absoluteAssetUrl(url),
        x: Number(image.getAttribute('x')),
        y: Number(image.getAttribute('y')),
        width: Number(image.getAttribute('width')),
        height: Number(image.getAttribute('height'))
      };
    }

    buildSaveRequest(state, design) {
      const rendered = this.lastState || state;
      const textBounds = state.name && rendered.textBounds ? {
        x: Number(rendered.textBounds.x.toFixed(3)),
        y: Number(rendered.textBounds.y.toFixed(3)),
        width: Number(rendered.textBounds.width.toFixed(3)),
        height: Number(rendered.textBounds.height.toFixed(3))
      } : null;
      return {
        product: this.dataset.productKey,
        design,
        artwork: {
          textX: state.name ? Number(this.previewName.getAttribute('x')) : 0,
          textY: state.name ? Number(this.previewName.getAttribute('y')) : 0,
          fontUrl: state.name ? this.absoluteAssetUrl(state.fontUrl) : '',
          textBounds,
          flag: this.imagePlacement(this.previewFlag, state.flagUrl),
          logo: this.imagePlacement(this.previewLogo, state.logoUrl)
        }
      };
    }

    async saveDesignAndSubmit(submitter) {
      const endpoint = this.dataset.apiUrl;
      if (!endpoint) {
        this.setSaveStatus(this.message('designServiceMissing'), 'error');
        return;
      }

      const state = this.currentState();
      const design = JSON.parse(this.propertyInputs.design.value);
      const fingerprint = this.designFingerprint(design);
      if (fingerprint === this.savedFingerprint && this.propertyInputs.preview.value) {
        this.resumeFormSubmit(submitter);
        return;
      }

      this.setFormSaving(true);
      this.setSaveStatus(this.message('saving'), 'saving');
      let designSaved = false;
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(this.buildSaveRequest(state, design))
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.designId || !result.previewUrl) {
          const message = Array.isArray(result.errors) ? result.errors[0] : '';
          throw new Error(message || this.message('saveFailed'));
        }

        const latestDesign = JSON.parse(this.propertyInputs.design.value);
        if (this.designFingerprint(latestDesign) !== fingerprint) {
          throw new Error(this.message('changedWhileSaving'));
        }

        design.id = result.designId;
        this.designId = result.designId;
        this.propertyInputs.id.value = result.designId;
        this.propertyInputs.design.value = JSON.stringify(design);
        this.propertyInputs.preview.value = result.previewUrl;
        this.savedFingerprint = fingerprint;
        this.setSaveStatus(this.message('saved'), 'ok');
        designSaved = true;
      } catch (error) {
        this.setSaveStatus(error.message || this.message('saveFailed'), 'error');
        this.saveStatus?.scrollIntoView({behavior: 'smooth', block: 'center'});
      } finally {
        this.setFormSaving(false);
      }

      if (designSaved) this.resumeFormSubmit(submitter);
    }

    resumeFormSubmit(submitter) {
      this.allowNextSubmit = true;
      if (submitter && this.form?.contains(submitter)) this.form.requestSubmit(submitter);
      else this.form?.requestSubmit();
    }

    buildFlagPicker() {
      if (!this.flagInput || !this.flagOptions || this.flagOptions.children.length) return;

      const options = [...this.flagInput.options].map((option) => ({
        code: option.value,
        name: option.dataset.countryName || option.textContent.trim(),
        url: option.dataset.flagUrl || ''
      }));

      const sorted = [options[0], ...options.slice(1).sort((a, b) => a.name.localeCompare(b.name))];
      const fragment = document.createDocumentFragment();

      sorted.forEach((country) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.flagValue = country.code;
        button.dataset.flagSearchValue = `${country.name} ${country.code}`.toLocaleLowerCase();

        if (country.url) {
          const image = document.createElement('img');
          image.src = country.url;
          image.alt = '';
          image.width = 32;
          image.height = 22;
          image.loading = 'lazy';
          button.appendChild(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'product-configurator__flag-placeholder';
          placeholder.textContent = '—';
          placeholder.setAttribute('aria-hidden', 'true');
          button.appendChild(placeholder);
        }

        const label = document.createElement('span');
        label.textContent = country.name;
        button.appendChild(label);

        if (country.code) {
          const code = document.createElement('small');
          code.textContent = country.code;
          button.appendChild(code);
        }

        button.addEventListener('click', () => this.selectFlag(country.code));
        item.appendChild(button);
        fragment.appendChild(item);
      });

      this.flagOptions.appendChild(fragment);
      this.updateFlagSelection();
    }

    toggleFlagPanel() {
      if (this.flagPanel.hidden) this.openFlagPanel();
      else this.closeFlagPanel();
    }

    openFlagPanel() {
      this.flagPanel.hidden = false;
      this.flagToggle.setAttribute('aria-expanded', 'true');
      this.flagSearch.value = '';
      this.filterFlags();
      requestAnimationFrame(() => this.flagSearch.focus());
    }

    closeFlagPanel() {
      if (!this.flagPanel) return;
      this.flagPanel.hidden = true;
      this.flagToggle?.setAttribute('aria-expanded', 'false');
    }

    filterFlags() {
      const term = this.flagSearch.value.trim().toLocaleLowerCase();
      let visibleCount = 0;

      this.flagOptions.querySelectorAll('li').forEach((item) => {
        const button = item.querySelector('button');
        const visible = !term || button.dataset.flagSearchValue.includes(term);
        item.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      this.flagEmpty.hidden = visibleCount > 0;
    }

    selectFlag(value) {
      this.flagInput.value = value;
      this.flagInput.dispatchEvent(new Event('change', {bubbles: true}));
      this.updateFlagSelection();
      this.closeFlagPanel();
      this.flagToggle.focus();
    }

    updateFlagSelection() {
      const option = this.flagInput?.selectedOptions[0];
      if (!option) return;
      const media = this.querySelector('[data-flag-selected-media]');
      const name = this.querySelector('[data-flag-selected-name]');
      const url = option.dataset.flagUrl || '';

      if (name) name.textContent = option.dataset.countryName || option.textContent.trim();
      if (!media) return;

      if (url) {
        const image = document.createElement('img');
        image.src = url;
        image.alt = '';
        image.width = 32;
        image.height = 22;
        image.dataset.flagSelectedMedia = '';
        media.replaceWith(image);
      } else if (media.tagName === 'IMG') {
        const placeholder = document.createElement('span');
        placeholder.className = 'product-configurator__flag-placeholder';
        placeholder.textContent = '—';
        placeholder.dataset.flagSelectedMedia = '';
        placeholder.setAttribute('aria-hidden', 'true');
        media.replaceWith(placeholder);
      }
    }

    selected(inputs) {
      return [...inputs].find((input) => input.checked) || inputs[0];
    }

    setImage(image, url) {
      if (!image) return;
      if (url) {
        image.setAttribute('href', url);
        image.removeAttribute('hidden');
      } else {
        image.removeAttribute('href');
        image.setAttribute('hidden', '');
      }
    }

    setBox(image, box) {
      if (!image || !box) return;
      const [x, y, width, height] = box;
      image.setAttribute('x', String(x));
      image.setAttribute('y', String(y));
      image.setAttribute('width', String(width));
      image.setAttribute('height', String(height));
    }

    applyLayout(state) {
      const hasLogo = Boolean(state.logoUrl);
      const layoutInput = this.selected(this.layoutInputs);
      const margin = Number(this.dataset.safeMarginMm) || 0;
      const widthMm = Number(this.dataset.widthMm) || 0;
      const heightMm = Number(this.dataset.heightMm) || 0;
      const logoBox = hasLogo && layoutInput
        ? [Number(layoutInput.dataset.logoX), Number(layoutInput.dataset.logoY), Number(layoutInput.dataset.logoWidth), Number(layoutInput.dataset.logoHeight)]
        : null;
      const textY = layoutInput
        ? Number(hasLogo ? layoutInput.dataset.textY : layoutInput.dataset.textYWithoutLogo)
        : Number(this.dataset.heightMm) / 2;
      const textRegion = [margin, widthMm - margin];

      if (logoBox) {
        const [x, y, width, height] = logoBox;
        const scale = state.logoSize / 100;
        const scaledWidth = width * scale;
        const scaledHeight = height * scale;
        const logoX = clamp(this.placement.logoX ?? x + (width - scaledWidth) / 2, margin, widthMm - margin - scaledWidth);
        const logoY = clamp(this.placement.logoY ?? y + (height - scaledHeight) / 2, margin, heightMm - margin - scaledHeight);
        if (this.placement.logoX !== null) this.placement.logoX = logoX;
        if (this.placement.logoY !== null) this.placement.logoY = logoY;
        state.logoBox = [logoX, logoY, scaledWidth, scaledHeight];
        this.setBox(this.previewLogo, state.logoBox);
      } else {
        state.logoBox = null;
      }

      state.textY = this.placement.textY ?? textY;
      state.textRegion = textRegion;
      state.maximumLineWidth = textRegion[1] - textRegion[0];
    }

    fitName(state) {
      if (!this.profile || this.profile.text.mode === 0) {
        this.invalidName = false;
        this.previewName.setAttribute('hidden', '');
        this.previewFlag?.setAttribute('hidden', '');
        this.setFitStatus('', 'neutral');
        state.actualSize = 0;
        state.textBounds = null;
        return;
      }

      this.previewName.removeAttribute('hidden');
      const minimumSize = Number(this.profile.text.minimumSize);
      let actualSize = state.requestedSize;
      this.previewName.setAttribute('font-size', String(actualSize));

      const measureLine = () => {
        const textWidth = this.previewName.getComputedTextLength();
        const flagHeight = state.flagUrl ? actualSize : 0;
        const flagWidth = state.flagUrl ? actualSize * 1.5 : 0;
        const gap = state.flagUrl ? Math.max(1.5, actualSize * 0.18) : 0;
        return {textWidth, flagHeight, flagWidth, gap, totalWidth: textWidth + flagWidth + gap};
      };

      let metrics = measureLine();
      while (actualSize > minimumSize && metrics.totalWidth > state.maximumLineWidth) {
        actualSize = roundHalf(Math.max(minimumSize, actualSize - 0.5));
        this.previewName.setAttribute('font-size', String(actualSize));
        metrics = measureLine();
      }

      const stillTooWide = metrics.totalWidth > state.maximumLineWidth + 0.1;
      this.invalidName = Boolean(state.name) && stillTooWide;
      state.actualSize = actualSize;
      this.positionNameAndFlag(state, metrics);

      if (!state.name) {
        this.setFitStatus(this.message('enterName'), 'neutral');
      } else if (stillTooWide) {
        this.setFitStatus(this.message('nameTooLong'), 'error');
      } else if (actualSize < state.requestedSize) {
        this.setFitStatus(this.message('autoReduced', {size: actualSize, margin: this.dataset.safeMarginMm}), 'adjusted');
      } else {
        this.setFitStatus(this.message('fitOk', {margin: this.dataset.safeMarginMm}), 'ok');
      }

      this.nameInput?.setCustomValidity(this.invalidName ? this.message('invalidName') : '');
      if (this.fontSizeOutput) {
        this.fontSizeOutput.textContent = actualSize < state.requestedSize
          ? `${state.requestedSize} mm → ${actualSize} mm`
          : `${state.requestedSize} mm`;
      }
    }

    positionNameAndFlag(state, metrics) {
      const [regionLeft, regionRight] = state.textRegion;
      const safeTop = Number(this.dataset.safeMarginMm) || 0;
      const safeBottom = Number(this.dataset.heightMm) - safeTop;
      const lineHeight = state.actualSize;
      state.textY = clamp(state.textY, safeTop + lineHeight / 2, safeBottom - lineHeight / 2);
      let lineLeft = this.placement.textX === null ? regionLeft : this.placement.textX - metrics.totalWidth / 2;

      if (this.placement.textX === null && state.alignment === 'center') {
        lineLeft = regionLeft + (state.maximumLineWidth - metrics.totalWidth) / 2;
      } else if (this.placement.textX === null && state.alignment === 'right') {
        lineLeft = regionRight - metrics.totalWidth;
      }
      lineLeft = clamp(lineLeft, regionLeft, Math.max(regionLeft, regionRight - metrics.totalWidth));
      state.textCenterX = lineLeft + metrics.totalWidth / 2;
      state.textCenterY = state.textY;
      state.textBounds = {x: lineLeft, y: state.textY - lineHeight / 2, width: metrics.totalWidth, height: lineHeight};
      if (this.placement.textX !== null) this.placement.textX = state.textCenterX;
      if (this.placement.textY !== null) this.placement.textY = state.textCenterY;
      this.previewName.setAttribute('y', String(state.textY));

      this.previewName.setAttribute('text-anchor', 'start');
      if (!state.flagUrl) {
        this.previewName.setAttribute('x', String(lineLeft));
        return;
      }

      const flagY = state.textY - metrics.flagHeight / 2;
      let flagX;
      let textX;

      if (state.flagSide === 'right') {
        textX = lineLeft;
        flagX = lineLeft + metrics.textWidth + metrics.gap;
      } else {
        flagX = lineLeft;
        textX = lineLeft + metrics.flagWidth + metrics.gap;
      }

      this.previewName.setAttribute('x', String(textX));
      this.setBox(this.previewFlag, [flagX, flagY, metrics.flagWidth, metrics.flagHeight]);
    }

    setFitStatus(message, status) {
      if (!this.fitStatus) return;
      this.fitStatus.textContent = message;
      this.fitStatus.dataset.status = status;
    }

    currentState() {
      const colorInput = this.selected(this.colorInputs);
      const textColorInput = this.selected(this.textColorInputs);
      const logoInput = this.selected(this.logoInputs);
      const fontInput = this.selected(this.fontInputs);
      const layoutInput = this.selected(this.layoutInputs);
      const alignmentInput = this.selected(this.alignmentInputs);
      const flagSideInput = this.selected(this.flagSideInputs);
      const flagOption = this.flagInput?.selectedOptions[0];
      const name = this.profile?.text.mode === 0 ? '' : (this.nameInput?.value.trim() || '');
      const uppercase = Boolean(this.uppercaseInput?.checked);
      const colorKey = this.profile?.productColors.mode === 0 ? '' : (colorInput?.value || '');
      const colorValue = this.profile?.productColors.mode === 0 ? 'none' : (colorInput?.dataset.colorValue || '#111111');
      const textColorKey = this.profile?.textColors.mode === 0 ? '' : (textColorInput?.value || '');
      const fontKey = this.profile?.fonts.mode === 0 ? '' : (fontInput?.value || '');
      const layoutKey = this.profile?.layouts.mode === 0 ? '' : (layoutInput?.value || '');
      const flagEnabled = this.profile?.flags.mode !== 0;
      const logoEnabled = this.profile?.logos.mode !== 0;

      return {
        colorKey,
        colorName: colorInput?.dataset.optionLabel || '',
        colorValue,
        isLight: this.isLightColor(colorValue),
        textColorKey,
        textColorName: textColorInput?.dataset.optionLabel || '',
        textColorValue: textColorInput?.dataset.textColorValue || '#ffffff',
        name,
        previewText: this.profile?.text.mode === 0 ? '' : (uppercase ? (name || this.message('yourName')).toLocaleUpperCase() : (name || this.message('yourName'))),
        uppercase,
        fontKey,
        fontName: fontInput?.dataset.optionLabel || '',
        fontFamily: fontInput?.dataset.fontFamily || 'Inter, sans-serif',
        fontWeight: Number(fontInput?.dataset.fontWeight || 700),
        fontUrl: fontInput?.dataset.fontUrl || '',
        requestedSize: Number(this.fontSizeInput?.value || this.profile?.text.defaultSize || 0),
        layout: layoutKey,
        layoutName: layoutInput?.dataset.optionLabel || '',
        alignment: alignmentInput?.value || this.profile?.text.defaultAlignment || '',
        flagSide: flagEnabled ? (flagSideInput?.value || this.profile?.flags.defaultPosition || '') : '',
        flagCode: flagEnabled ? (flagOption?.value || '') : '',
        flagName: flagOption?.dataset.countryName || this.message('noFlag'),
        flagUrl: flagEnabled ? (flagOption?.dataset.flagUrl || '') : '',
        logoKey: logoEnabled ? (logoInput?.value || '') : '',
        logoName: logoEnabled ? (logoInput?.dataset.optionLabel || this.message('noLogo')) : '',
        logoUrl: logoEnabled ? (logoInput?.dataset.logoUrl || '') : '',
        logoSize: logoEnabled ? Number(this.logoSizeInput?.value || this.profile?.logos.defaultSizePercent || 100) : 0
      };
    }

    isLightColor(value) {
      if (!/^#[0-9a-f]{6}$/i.test(value)) return false;
      const red = Number.parseInt(value.slice(1, 3), 16);
      const green = Number.parseInt(value.slice(3, 5), 16);
      const blue = Number.parseInt(value.slice(5, 7), 16);
      return (red * 299 + green * 587 + blue * 114) / 1000 > 190;
    }

    updateProperties(state) {
      const textSize = state.actualSize < state.requestedSize
        ? `${state.requestedSize} mm (auto-fit ${state.actualSize} mm)`
        : `${state.actualSize} mm`;

      this.propertyInputs.color.value = state.colorName;
      this.propertyInputs.name.value = state.name;
      const hasTextFeature = this.profile?.text.mode !== 0;
      this.propertyInputs.textColor.value = hasTextFeature ? state.textColorName : '';
      this.propertyInputs.font.value = hasTextFeature ? state.fontName : '';
      this.propertyInputs.fontSize.value = hasTextFeature ? textSize : '';
      this.propertyInputs.alignment.value = hasTextFeature && state.alignment
        ? state.alignment[0].toUpperCase() + state.alignment.slice(1)
        : '';
      this.propertyInputs.textCase.value = hasTextFeature ? (state.uppercase ? this.message('uppercase') : this.message('asEntered')) : '';
      this.propertyInputs.layout.value = state.layoutName || LAYOUT_LABELS[state.layout] || '';
      this.propertyInputs.flag.value = this.profile?.flags.mode === 0
        ? ''
        : (state.flagCode ? `${state.flagName} (${state.flagCode})` : this.message('noFlag'));
      this.propertyInputs.flagSide.value = state.flagCode
        ? (state.flagSide === 'right' ? this.message('rightOfName') : this.message('leftOfName'))
        : '';
      this.propertyInputs.logo.value = state.logoName;
      this.propertyInputs.logoSize.value = state.logoKey ? `${state.logoSize}%` : '';

      this.propertyInputs.design.value = JSON.stringify({
        v: 6,
        id: this.designId,
        s: [Number(this.dataset.widthMm), Number(this.dataset.heightMm), Number(this.dataset.safeMarginMm)],
        loc: this.locale,
        c: state.colorKey,
        n: state.name,
        tc: state.textColorKey,
        f: state.fontKey,
        w: state.fontWeight,
        z: state.actualSize,
        a: state.alignment,
        u: state.uppercase ? 1 : 0,
        fl: state.flagCode,
        fp: state.flagSide,
        lg: state.logoKey,
        lz: state.logoSize,
        ly: state.layout,
        tp: state.name ? [Number(this.previewName.getAttribute('x')), Number(this.previewName.getAttribute('y'))] : [],
        tb: state.name && state.textBounds ? [state.textBounds.x, state.textBounds.y, state.textBounds.width, state.textBounds.height].map((value) => Number(value.toFixed(3))) : [],
        lp: state.logoUrl && state.logoBox ? state.logoBox.map((value) => Number(value.toFixed(3))) : []
      });
    }

    update() {
      const state = this.currentState();
      this.surface?.setAttribute('fill', state.colorValue);
      this.toggleAttribute('data-light-color', state.isLight);
      this.previewName.textContent = state.previewText;
      this.previewName.setAttribute('font-family', state.fontFamily);
      this.previewName.setAttribute('font-weight', String(state.fontWeight));
      this.previewName.setAttribute('fill', state.textColorValue);

      this.setImage(this.previewFlag, state.flagUrl);
      this.setImage(this.previewLogo, state.logoUrl);
      this.applyLayout(state);
      this.fitName(state);
      this.updateProperties(state);
      this.updateFlagSelection();
      if (this.logoSizeOutput) this.logoSizeOutput.textContent = `${state.logoSize}%`;
      this.lastState = state;
      this.updateHitAreas();
      this.updateSelection();
      this.positionDirectTextEditor();

      if (this.profile?.text.mode !== 0 && state.fontName && document.fonts?.load) {
        const fontQuery = `${state.fontWeight} ${Math.max(24, state.requestedSize * 4)}px "${state.fontName}"`;
        document.fonts.load(fontQuery, state.previewText).then(() => {
          const latest = this.currentState();
          if (latest.fontName !== state.fontName || latest.previewText !== state.previewText) return;
          this.applyLayout(latest);
          this.fitName(latest);
          this.updateProperties(latest);
          this.lastState = latest;
          this.updateHitAreas();
          this.updateSelection();
          this.positionDirectTextEditor();
        }).catch(() => {});
      }
    }
  }

  customElements.define('product-configurator', ProductConfigurator);
})();
